import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import es from "./es";
import en from "./en";
import { applySiteLocale } from "../content/site";

export const UI_LANGS = ["en", "es", "fr", "de", "it", "pt"];
const EXTRA_LANGS = ["fr", "de", "it", "pt"];
const DICTS = { es, en };
const loadedExtras = new Set();
const LanguageContext = createContext(null);

// Extra locales are lazy-loaded to keep the initial bundle small.
async function loadLocale(l) {
  if (!EXTRA_LANGS.includes(l) || loadedExtras.has(l)) return;
  const [dict, site] = await Promise.all([
    import(`./${l}.json`),
    import(`../content/site.${l}.json`),
  ]);
  DICTS[l] = dict.default;
  applySiteLocale(l, site.default);
  loadedExtras.add(l);
}

function resolve(obj, path) {
  return path.split(".").reduce((acc, k) => (acc == null ? undefined : acc[k]), obj);
}

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      const stored = localStorage.getItem("ep_lang");
      if (UI_LANGS.includes(stored)) return stored;
      const nav = (navigator.language || navigator.userLanguage || "en").toLowerCase().slice(0, 2);
      return UI_LANGS.includes(nav) ? nav : "en";
    } catch {
      return "en";
    }
  });
  const [, setLocaleV] = useState(0);

  useEffect(() => {
    if (EXTRA_LANGS.includes(lang) && !loadedExtras.has(lang)) {
      loadLocale(lang).then(() => setLocaleV((v) => v + 1)).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const changeLang = useCallback((l) => {
    if (!UI_LANGS.includes(l)) return;
    const apply = () => {
      setLang(l);
      setLocaleV((v) => v + 1);
      try {
        localStorage.setItem("ep_lang", l);
      } catch {}
      document.documentElement.lang = l;
    };
    if (EXTRA_LANGS.includes(l) && !loadedExtras.has(l)) loadLocale(l).then(apply).catch((e) => { console.error("loadLocale failed", l, e); apply(); });
    else apply();
  }, []);

  // Fallback chain: requested lang -> English -> Spanish (admin-only keys).
  const t = useCallback(
    (path) => {
      let val = resolve(DICTS[lang], path);
      if (val === undefined) val = resolve(DICTS.en, path);
      return val === undefined ? resolve(DICTS.es, path) : val;
    },
    [lang]
  );

  return (
    <LanguageContext.Provider value={{ lang, changeLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useI18n = () => useContext(LanguageContext);
